import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const Hero = ({ title, subtitle, description, showButtons = true, backgroundImage = null, variant = 'default' }) => {
  const { t } = useLanguage();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);
  const [partnerForm, setPartnerForm] = useState({
    nomeSolicitante: '',
    nomeOrganizacao: '',
    telefone: '',
    email: ''
  });

  // Imagens do carrossel - profissionais da saúde africanos
  const carouselImages = [
    {
      url: '/img/apresentacao.png',
      alt: t('home.hero.carousel.consulting.alt'),
      title: t('home.hero.carousel.consulting.title')
    },
    {
      url: '/img/pesquisa.png',
      alt: t('home.hero.carousel.research.alt'),
      title: t('home.hero.carousel.research.title')
    },
    {
      url: '/img/resultado.png',
      alt: t('home.hero.carousel.results.alt'),
      title: t('home.hero.carousel.results.title')
    }
  ];

  // Auto-rotate carousel
  useEffect(() => {
    if (variant === 'home') {
      const interval = setInterval(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % carouselImages.length);
      }, 5000); // Change image every 5 seconds

      return () => clearInterval(interval);
    }
  }, [carouselImages.length, variant]);

  if (variant === 'home') {

    // Handle form input changes
    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setPartnerForm(prev => ({
        ...prev,
        [name]: value
      }));
    };

    // Handle form submission
    const handleSubmit = (e) => {
      e.preventDefault();
      console.log('Partner form submitted:', partnerForm);
      // Here you would typically send the data to your API
      alert('Solicitação enviada com sucesso! Entraremos em contato em breve.');
      setIsPartnerModalOpen(false);
      setPartnerForm({
        nomeSolicitante: '',
        nomeOrganizacao: '',
        telefone: '',
        email: ''
      });
    };

    return (
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Carousel Background Images */}
        <div className="absolute inset-0">
          {carouselImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                index === currentImageIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={image.url}
                alt={image.alt}
                className="w-full h-full object-cover"
                loading={index === 0 ? 'eager' : 'lazy'}
              />
              {/* Dark overlay for better text readability */}
              <div className="absolute inset-0 bg-black bg-opacity-50"></div>
            </div>
          ))}
        </div>

        {/* Animated background elements - Responsive sizes */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-32 h-32 sm:w-48 sm:h-48 md:w-64 md:h-64 lg:w-96 lg:h-96 bg-emerald-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
          <div className="absolute top-1/3 right-1/4 w-32 h-32 sm:w-48 sm:h-48 md:w-64 md:h-64 lg:w-96 lg:h-96 bg-teal-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1000"></div>
          <div className="absolute bottom-1/4 left-1/2 w-32 h-32 sm:w-48 sm:h-48 md:w-64 md:h-64 lg:w-96 lg:h-96 bg-cyan-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-2000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-8 sm:py-12 md:py-16 lg:py-20">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 sm:gap-8 md:gap-10 lg:gap-12 items-center">
            {/* Left Content - Fully Responsive */}
            <div className="text-white space-y-4 sm:space-y-6 md:space-y-8 order-2 xl:order-1">
              <div className="space-y-2 sm:space-y-3 md:space-y-4">
                <div className="inline-flex items-center px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 bg-emerald-500/20 backdrop-blur-sm rounded-full border border-emerald-400/30">
                  <span className="text-emerald-300 text-xs sm:text-sm font-medium">{t('home.hero.badge')}</span>
                </div>

                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-bold leading-tight">
                  <span className="block text-white">{t('home.hero.title')}</span>
                  <span className="block bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
                    {t('home.hero.titleHighlight')}
                  </span>
                </h1>
              </div>

              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-300 leading-relaxed max-w-2xl">
                {t('home.hero.description')}
              </p>

              {showButtons && (
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <Link
                    to="/sobre"
                    className="group bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-4 sm:px-6 md:px-8 py-2 sm:py-3 md:py-4 rounded-lg sm:rounded-xl font-semibold hover:from-emerald-600 hover:to-teal-700 transition-all duration-300 shadow-2xl hover:shadow-emerald-500/25 flex items-center justify-center space-x-2 text-sm sm:text-base"
                  >
                    <span>{t('home.hero.buttons.learnMore')}</span>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </Link>
                  <button
                    onClick={() => setIsPartnerModalOpen(true)}
                    className="group border-2 border-white/30 text-white px-4 sm:px-6 md:px-8 py-2 sm:py-3 md:py-4 rounded-lg sm:rounded-xl font-semibold hover:bg-white hover:text-slate-900 transition-all duration-300 backdrop-blur-sm flex items-center justify-center space-x-2 text-sm sm:text-base"
                  >
                    <span>{t('home.hero.buttons.becomePartner')}</span>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Stats - Responsive Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 pt-4 sm:pt-6 md:pt-8">
                <div className="text-center">
                  <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-emerald-400 mb-1 sm:mb-2">10K+</div>
                  <div className="text-gray-300 text-xs sm:text-sm">{t('home.hero.stats.impacted')}</div>
                </div>
                <div className="text-center">
                  <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-emerald-400 mb-1 sm:mb-2">10+</div>
                  <div className="text-gray-300 text-xs sm:text-sm">{t('home.hero.stats.projectsDone')}</div>
                </div>
                <div className="text-center">
                  <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-emerald-400 mb-1 sm:mb-2">20+</div>
                  <div className="text-gray-300 text-xs sm:text-sm">{t('home.hero.stats.volunteers')}</div>
                </div>
                <div className="text-center">
                  <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-emerald-400 mb-1 sm:mb-2">15</div>
                  <div className="text-gray-300 text-xs sm:text-sm">{t('home.hero.stats.citiesServed')}</div>
                </div>
              </div>
            </div>

            {/* Right Content - Hero Image/Visual - Fully Responsive */}
            <div className="relative order-1 xl:order-2 w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl mx-auto xl:mx-0">
              <div className="relative z-10">
                {/* Main Image Placeholder - Responsive Size with Viewport Constraints */}
                <div className="relative rounded-xl sm:rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl w-full h-auto max-h-[50vh] sm:max-h-[60vh] md:max-h-[70vh] lg:max-h-[80vh]">
                  <div className="aspect-square sm:aspect-[4/5] md:aspect-[4/5] lg:aspect-[4/5] bg-gradient-to-br from-emerald-500/20 to-teal-500/20 backdrop-blur-sm border border-white/20 flex items-center justify-center w-full h-full">
                    <div className="text-center text-white/80 p-2 sm:p-4 md:p-6 w-full h-full flex flex-col justify-center">
                      <svg className="w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 xl:w-24 xl:h-24 mx-auto mb-1 sm:mb-2 md:mb-3 lg:mb-4 text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      <p className="text-xs sm:text-sm md:text-base lg:text-lg font-medium leading-tight">{t('home.healthProfessionals.title')}</p>
                      <p className="text-xs sm:text-sm opacity-70 leading-tight">{t('home.healthProfessionals.subtitle')}</p>
                    </div>
                  </div>
                </div>

                {/* Floating Elements - Responsive Sizes */}
                <div className="absolute -top-2 -right-2 sm:-top-3 sm:-right-3 md:-top-4 md:-right-4 lg:-top-6 lg:-right-6 w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 xl:w-24 xl:h-24 bg-emerald-400/20 rounded-lg sm:rounded-xl md:rounded-2xl backdrop-blur-sm border border-emerald-400/30 flex items-center justify-center animate-bounce">
                  <svg className="w-4 h-4 sm:w-6 sm:h-6 md:w-8 md:h-8 lg:w-10 lg:h-10 xl:w-12 xl:h-12 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>

                <div className="absolute -bottom-2 -left-2 sm:-bottom-3 sm:-left-3 md:-bottom-4 md:-left-4 lg:-bottom-6 lg:-left-6 w-6 h-6 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-16 lg:h-16 xl:w-20 xl:h-20 bg-teal-400/20 rounded-lg sm:rounded-xl md:rounded-2xl backdrop-blur-sm border border-teal-400/30 flex items-center justify-center animate-pulse">
                  <svg className="w-3 h-3 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-8 lg:h-8 xl:w-10 xl:h-10 text-teal-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Carousel Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3 z-10">
          {carouselImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentImageIndex
                  ? 'bg-white scale-125'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Ir para imagem ${index + 1}`}
            />
          ))}
        </div>

        {/* Carousel Navigation */}
        <button
          onClick={() => setCurrentImageIndex((prevIndex) =>
            prevIndex === 0 ? carouselImages.length - 1 : prevIndex - 1
          )}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-3 transition-all duration-300"
          aria-label="Imagem anterior"
        >
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <button
          onClick={() => setCurrentImageIndex((prevIndex) =>
            prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1
          )}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-3 transition-all duration-300"
          aria-label="Próxima imagem"
        >
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Partner Modal */}
        {isPartnerModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Seja um Parceiro</h2>
                  <button
                    onClick={() => setIsPartnerModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <p className="text-gray-600 mb-6">
                  Preencha o formulário abaixo para se tornar um parceiro da Conexão Saúde e juntos promovermos a saúde na Guiné-Bissau.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="nomeSolicitante" className="block text-sm font-medium text-gray-700 mb-2">
                      Nome do Solicitante *
                    </label>
                    <input
                      type="text"
                      id="nomeSolicitante"
                      name="nomeSolicitante"
                      value={partnerForm.nomeSolicitante}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      placeholder="Seu nome completo"
                    />
                  </div>

                  <div>
                    <label htmlFor="nomeOrganizacao" className="block text-sm font-medium text-gray-700 mb-2">
                      Nome da Organização *
                    </label>
                    <input
                      type="text"
                      id="nomeOrganizacao"
                      name="nomeOrganizacao"
                      value={partnerForm.nomeOrganizacao}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      placeholder="Nome da sua organização"
                    />
                  </div>

                  <div>
                    <label htmlFor="telefone" className="block text-sm font-medium text-gray-700 mb-2">
                      Telefone *
                    </label>
                    <input
                      type="tel"
                      id="telefone"
                      name="telefone"
                      value={partnerForm.telefone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      placeholder="+245 966 065 555"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      E-mail *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={partnerForm.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      placeholder="seu@email.com"
                    />
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setIsPartnerModalOpen(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-200"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors duration-200"
                    >
                      Enviar Solicitação
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </section>
    );
  }

  // Default variant for other pages - Fully Responsive
  return (
    <section className={`relative ${backgroundImage ? 'bg-cover bg-center bg-no-repeat' : 'bg-gradient-to-r from-emerald-600 to-teal-800'}`}
             style={backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : {}}>
      {backgroundImage && (
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
      )}
      <div className="relative max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-12 sm:py-16 md:py-20 lg:py-24">
        <div className="text-center">
          <h1 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 ${backgroundImage ? 'text-white' : 'text-white'}`}>
            {title}
          </h1>
          {subtitle && (
            <h2 className={`text-lg sm:text-xl md:text-2xl font-semibold mb-4 sm:mb-6 ${backgroundImage ? 'text-gray-200' : 'text-emerald-100'}`}>
              {subtitle}
            </h2>
          )}
          {description && (
            <p className={`text-sm sm:text-base md:text-lg lg:text-xl mb-6 sm:mb-8 max-w-3xl mx-auto px-4 ${backgroundImage ? 'text-gray-300' : 'text-emerald-100'}`}>
              {description}
            </p>
          )}
          {showButtons && (
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
              <Link
                to="/sobre"
                className="bg-white text-emerald-600 hover:bg-gray-100 font-semibold py-2 sm:py-3 px-6 sm:px-8 rounded-lg transition-colors duration-200 shadow-lg text-sm sm:text-base"
              >
                Conheça Nossa Missão
              </Link>
              <Link
                to="/servicos"
                className="border-2 border-white text-white hover:bg-white hover:text-emerald-600 font-semibold py-2 sm:py-3 px-6 sm:px-8 rounded-lg transition-colors duration-200 text-sm sm:text-base"
              >
                Ver Serviços
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Hero;
